import React from "react";
import { FriendlyErrorPage } from "./FriendlyErrorPage";
import { reportError } from "../reporting/reporter";

interface State {
  hasError: boolean;
  error?: Error | undefined;
}

/**
 * Route-level error boundary. Wraps <Routes> so that a single page crash
 * doesn't unmount the shell (TopNav, BottomTabBar, AuthProvider). Offers
 * a "Back to home" recovery that navigates without a full reload, preserving
 * the driver's shift state / customer's session. The App-level ErrorBoundary
 * is still the outer net for AuthProvider / context crashes.
 *
 * Resets its hasError state whenever the URL changes via the keyed remount
 * in AppShell — a driver who nav'd away from a crashed page lands on a
 * fresh render.
 */
export class RouteErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    void reportError({
      message: error.message,
      stack:
        (info.componentStack
          ? `${error.stack ?? ""}\n\nComponent stack:${info.componentStack}`
          : error.stack) ?? "",
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <FriendlyErrorPage
          actionLabel="Back to home"
          onAction={() => {
            this.setState({ hasError: false, error: undefined });
            if (typeof window !== "undefined") window.location.href = "/";
          }}
        />
      );
    }
    return this.props.children;
  }
}
